import {
	Color,
	GlProgram,
	MeshSimple,
	Shader,
	Texture,
} from 'pixi.js';

// Art direction constant: portal bands stay consistent across editor and runtime.
const PORTAL_RIBBON_WIDTH = 0.4;

const VERTEX = `
in vec2 aPosition;
in vec2 aUV;

out vec2 vUV;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;

void main(void)
{
	mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
	gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
	vUV = aUV;
}`;

const FRAGMENT = `
in vec2 vUV;

uniform vec4 uPortalColor;
uniform float uTime;
uniform float uSeed;
uniform float uOpenProgress;

const float PORTAL_RIBBON_WIDTH = ${PORTAL_RIBBON_WIDTH.toFixed(1)};

float hash21(vec2 p)
{
	p = fract(p * vec2(123.34, 345.45));
	p += dot(p, p + 34.345);
	return fract(p.x * p.y);
}

float noise21(vec2 p)
{
	vec2 cell = floor(p);
	vec2 local = fract(p);
	local = local * local * (3.0 - 2.0 * local);

	return mix(
		mix(hash21(cell), hash21(cell + vec2(1.0, 0.0)), local.x),
		mix(hash21(cell + vec2(0.0, 1.0)), hash21(cell + vec2(1.0)), local.x),
		local.y
	);
}

float fbm(vec2 p)
{
	float value = 0.0;
	float amplitude = 0.5;
	mat2 turn = mat2(0.80, -0.60, 0.60, 0.80);

	for(int octave = 0; octave < 4; octave++) {
		value += noise21(p) * amplitude;
		p = turn * p * 2.03 + 9.17;
		amplitude *= 0.5;
	}

	return value;
}

mat2 rotate2d(float angle)
{
	float sine = sin(angle);
	float cosine = cos(angle);
	// GLSL matrix constructors are column-major. This ordering keeps sampled
	// liquid motion in the same rotational direction as the spiral phase.
	return mat2(cosine, sine, -sine, cosine);
}

void main(void)
{
	vec2 portal = (vUV - 0.5) * 2.0;
	float radius = length(portal);
	float angle = atan(portal.y, portal.x);
	float time = uTime * 1.30;

	float edgeNoise = noise21(portal * 5.3 + uSeed * 7.0);
	float boundary = 0.965
		+ sin(angle * 7.0 - time * 0.55 + uSeed * 19.0) * 0.018
		+ sin(angle * 13.0 - time * 0.37) * 0.010
		+ (edgeNoise - 0.5) * 0.025;
	float alpha = 1.0 - smoothstep(boundary - 0.018, boundary + 0.006, radius);
	if(alpha <= 0.001) discard;

	// This coordinate defines the handedness and pitch: tracing a band inward
	// increases screen-space angle, so the band arcs clockwise into the throat.
	// The negative time term rotates the bands themselves clockwise, while the
	// separate stream coordinate below carries surface detail farther inward.
	float vortexTurn = (1.0 - radius) * 1.15 + uSeed * 2.0;
	float vortexAngle = angle + vortexTurn;
	vec2 flow = rotate2d(vortexTurn) * portal;
	vec2 distortion = vec2(
		noise21(flow * 2.5 + vec2(0.0, uSeed * 13.0)),
		noise21(flow * 2.5 + vec2(7.1 + uSeed * 9.0, 0.0))
	) - 0.5;
	flow += distortion * (0.08 + radius * 0.06);

	float broad = fbm(flow * 2.8 + uSeed * 5.0);
	float detail = fbm(flow * 6.0 + uSeed * 11.0);
	float screwPhase = vortexAngle * 3.0
		+ radius * 14.0
		+ (broad - 0.5) * 1.8
		+ uSeed * 8.0
		- time * 0.55;
	// This second coordinate runs along the screw rather than across it. Its
	// negative time term advances packets clockwise (angle increases on screen)
	// and inward (radius decreases) while screwPhase stays constant.
	float streamPhase = angle * 3.0
		- radius * 5.0
		- time * 0.75
		+ (detail - 0.5) * 0.85;
	float stream = 0.5 + 0.5 * sin(streamPhase);
	float streamFine = 0.5 + 0.5 * sin(streamPhase * 2.0 + broad * 1.2);
	float spiral = 0.5 + 0.5 * sin(screwPhase);
	float spiralVisibility = smoothstep(0.17, 0.34, radius);
	float ribbonStart = 1.0 - PORTAL_RIBBON_WIDTH;
	float ribbonEnd = min(0.97, ribbonStart + 0.18);
	float ribbons = smoothstep(ribbonStart, ribbonEnd, spiral + (broad - 0.5) * 0.16)
		* spiralVisibility;
	float wispStart = min(0.98, ribbonEnd + 0.07);
	float wisps = smoothstep(wispStart, 0.99, spiral + (detail - 0.5) * 0.13)
		* spiralVisibility;
	float movingRibbons = ribbons * (0.70 + stream * 0.30);
	float movingWisps = wisps * (0.40 + streamFine * 0.60);

	float shade = 0.27 + broad * 0.22 + movingRibbons * 0.36 + movingWisps * 0.16;
	shade -= (1.0 - smoothstep(0.04, 0.45, radius)) * 0.06;

	vec3 selected = uPortalColor.rgb;
	// Keep every dark region visibly inside the selected-color family. The old
	// palette multiplied the throat down twice and produced an almost black hole;
	// the reference portal instead uses saturated dark, mid, lime, and white bands.
	vec3 deep = clamp(selected * 0.42 + vec3(0.035), 0.0, 1.0);
	vec3 shadow = mix(deep, selected, 0.12);
	vec3 midtone = selected;
	vec3 bright = mix(selected, vec3(1.0), 0.40);
	vec3 hot = mix(selected, vec3(1.0), 0.82);

	vec3 color = shadow;
	color = mix(color, midtone, smoothstep(0.28, 0.50, shade));
	color = mix(color, bright, smoothstep(0.54, 0.76, shade));
	color = mix(color, hot, smoothstep(0.81, 0.97, shade));
	float throat = 1.0 - smoothstep(0.025, 0.19, radius);
	float throatShade = 0.24 + spiral * 0.42 + stream * 0.22;
	vec3 throatColor = mix(deep, bright, throatShade);
	color = mix(color, throatColor, throat * 0.88);
	float throatRing = smoothstep(0.09, 0.18, radius)
		* (1.0 - smoothstep(0.18, 0.34, radius));
	color = mix(color, bright, throatRing * movingRibbons * 0.34);

	float rimMask = smoothstep(0.70, 0.91, radius)
		* (1.0 - smoothstep(boundary - 0.035, boundary + 0.004, radius));
	float rimPulse = 0.72 + 0.28 * sin(vortexAngle * 5.0 - time * 0.65 + detail * 3.0);
	color = mix(color, bright, rimMask * (0.46 + rimPulse * 0.24));

	float fleckField = noise21(flow * 8.0 + uSeed * 17.0);
	float flecks = smoothstep(0.88, 0.97, fleckField + movingWisps * 0.10)
		* smoothstep(0.48, 0.90, streamFine)
		* smoothstep(0.18, 0.62, radius)
		* (1.0 - smoothstep(0.92, 0.98, radius));
	color = mix(color, hot, flecks * 0.82);

	// A bright energy front travels from the throat to the rim while a newly
	// created portal stretches open. At rest uOpenProgress is one, so this adds
	// no cost to the established portal palette beyond a few scalar operations.
	float openingProgress = clamp(uOpenProgress, 0.0, 1.0);
	float ignition = sin(openingProgress * 3.14159265);
	float ignitionRadius = 0.12 + openingProgress * 0.82;
	float ignitionBand = 1.0 - smoothstep(0.0, 0.16, abs(radius - ignitionRadius));
	color = mix(color, hot, ignition * ignitionBand * 0.72);

	float pulse = 0.99 + sin(time * 0.80 + broad * 5.0) * 0.025;
	color = max(color * pulse, deep * 0.92);
	gl_FragColor = vec4(color * alpha, alpha);
}`;

export class PortalShader extends Shader {
	constructor(color, seed = 0) {
		super({
			glProgram: GlProgram.from({
				vertex: VERTEX,
				fragment: FRAGMENT,
				name: 'animated-portal-shader',
			}),
			resources: {
				portalUniforms: {
					uPortalColor: {
						value: new Float32Array([1, 1, 1, 1]),
						type: 'vec4<f32>',
					},
					uTime: {value: 0, type: 'f32'},
					uSeed: {value: seed, type: 'f32'},
					uOpenProgress: {value: 1, type: 'f32'},
				},
			},
		});
		this.setColor(color);
	}

	setColor(color) {
		const [red, green, blue] = new Color(color).toArray();
		this.resources.portalUniforms.uniforms.uPortalColor = new Float32Array([
			red,
			green,
			blue,
			1,
		]);
	}

	setTime(time) {
		this.resources.portalUniforms.uniforms.uTime = time;
	}

	setOpenProgress(progress) {
		this.resources.portalUniforms.uniforms.uOpenProgress = Math.max(
			0,
			Math.min(1, Number(progress) || 0),
		);
	}

}

export const createPortalMesh = ({
	height,
	color,
	seed = 0,
}) => {
	const width = height * 0.252;
	const visualHeight = height * 1.12;
	const halfWidth = width / 2;
	const halfHeight = visualHeight / 2;
	const shear = width * 0.075;
	const frontOffset = width * 0.035;
	const shader = new PortalShader(color, seed);
	const mesh = new MeshSimple({
		texture: Texture.WHITE,
		shader,
		vertices: new Float32Array([
			-halfWidth + shear + frontOffset, -halfHeight,
			halfWidth + shear + frontOffset, -halfHeight,
			halfWidth - shear + frontOffset, halfHeight,
			-halfWidth - shear + frontOffset, halfHeight,
		]),
		uvs: new Float32Array([
			0, 0,
			1, 0,
			1, 1,
			0, 1,
		]),
		indices: new Uint32Array([0, 1, 2, 0, 2, 3]),
	});
	mesh.label = 'animated-portal-surface';
	mesh.eventMode = 'none';
	mesh.autoUpdate = false;
	mesh.onRender = () => shader.setTime(performance.now() * 0.001);

	return {mesh, shader};
};
